import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  TemplateRef,
  computed,
  contentChild,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Autocomplete on a custom Payee input (Material plain-input pattern).
 *
 * CVA value is the selected option (object/primitive), not the typed string.
 * Typing is exposed via `(searchChange)` for async option loading.
 *
 * Override option markup with `#optionTemplate` (same pattern as payee-dropdown).
 *
 * @see https://v21.material.angular.dev/components/autocomplete/overview
 */
@Component({
  selector: 'payee-autocomplete',
  templateUrl: './payee-autocomplete.component.html',
  styleUrls: ['./payee-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PayeeAutocompleteComponent),
      multi: true,
    },
  ],
  imports: [
    NgTemplateOutlet,
    PayeeInputDirective,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    TranslateModule,
  ],
})
export class PayeeAutocompleteComponent implements ControlValueAccessor {
  private readonly inputRef =
    viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  readonly value = signal<unknown>(null);
  readonly displayText = signal('');
  disabled = false;

  @Output() readonly searchChange = new EventEmitter<string>();

  readonly options = input<unknown[]>([]);
  readonly placeholder = input('');
  readonly ariaLabel = input('');
  readonly requireSelection = input(true);
  readonly clearable = input(true);
  readonly showAllOptionsOnFocus = input(false);
  readonly displayWith = input<(value: any) => string>(v => v ? String(v) : '');
  readonly filterPredicate = input<(option: unknown, filter: string) => boolean>((option, filter) => {
    return this.displayWith()(option).toLowerCase().includes(filter.toLowerCase());
  });
  readonly optionTemplate =
    contentChild<TemplateRef<{ $implicit: unknown }>>('optionTemplate');
  private readonly ignoreFilter = signal(false);
  readonly filteredOptions = computed<unknown[]>(() => {
    const filter = this.displayText();
    if (!filter || this.ignoreFilter()) {
      return this.options();
    }

    return this.options().filter(option => this.filterPredicate()(option, filter));
  });
  readonly showClear = computed(() => {
    if (!this.clearable()) {
      return false;
    }

    return this.value() || this.displayText().trim().length > 0;
  });

  private onChange: (value: unknown) => void = () => { };
  private onTouched: () => void = () => { };

  onInput(event: Event): void {
    if (!(event instanceof InputEvent) || !(event.target instanceof HTMLInputElement)) {
      console.warn('Unexpected input event');
      return;
    }

    this.ignoreFilter.set(false);
    const text = event.target.value;
    this.displayText.set(text);
    this.searchChange.emit(text);
  }

  onFocus(): void {
    if (!this.showAllOptionsOnFocus()) {
      return;
    }

    this.ignoreFilter.set(true);
    queueMicrotask(() => this.inputRef().nativeElement.select());
  }

  onOpened(): void {
    if (
      this.showAllOptionsOnFocus()
      && this.displayText() === this.displayWith()(this.value())
    ) {
      this.ignoreFilter.set(true);
    }
  }

  onClosed(): void {
    this.onTouched();

    // Material emits `closed` when the option list becomes empty while typing.
    // Only restore the committed value when the user has left the input.
    const input = this.inputRef().nativeElement;
    if (document.activeElement === input) {
      return;
    }

    this.ignoreFilter.set(false);
    this.syncDisplayFromValue();
  }

  clear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.value.set(null);
    this.displayText.set('');
    this.onChange(null);
    this.onTouched();
    this.searchChange.emit('');
    this.inputRef().nativeElement.focus();
  }

  writeValue(value: unknown): void {
    this.value.set(value);
    this.syncDisplayFromValue();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  private syncDisplayFromValue(): void {
    this.displayText.set(this.displayWith()(this.value()));
  }
}
